import React, { useMemo, useCallback, memo } from "react";
import { ArrowUp, ArrowDown, Truck } from "lucide-react";
import { useProductionOrders } from "../hooks/useProductionData";
import { productionService } from "../services/productionService";
import { useAuth } from "../contexts/AuthContext";
import { logAction } from "../services/logService";
import TransitItemRow from "../components/production/TransitItemRow";
import { useProductionContext } from "../contexts/ProductionContext";

const RenderList = memo(({ title, items, icon: Icon, colorClass, findCatalogItem, onToggle }) => (
  <div className={`flex-1 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col h-full`}>
    <div className={`p-4 border-b border-slate-100 flex justify-between items-center ${colorClass}`}>
      <h3 className="font-bold flex items-center gap-2 text-slate-700">
        <Icon size={20} /> {title}
      </h3>
      <span className="bg-white/50 px-2 py-0.5 rounded text-xs font-bold text-slate-800">
        {items.length} itens
      </span>
    </div>

    <div className="overflow-y-auto p-2 space-y-2 flex-1 custom-scrollbar bg-slate-50/50">
      {items.length === 0 && (
        <div className="text-center py-10 text-slate-400 text-sm">
          Nenhum item neste fluxo.
        </div>
      )}
      {items.map((item) => {
        const catalogItem = findCatalogItem ? findCatalogItem(item.sku) : null;
        return (
          <TransitItemRow 
            key={item.id} 
            item={item} 
            catalogItem={catalogItem} 
            onToggle={onToggle} 
          />
        );
      })}
    </div>
  </div>
));

export default function TransitTab({ findCatalogItem }) {
  const { orders, loading, refreshOrders, setOrders } = useProductionContext();
  const { user } = useAuth();

  // --- CORREÇÃO 1: Filtra por 'subindo' e 'descendo' ---
  const transitItems = useMemo(() => {
    return orders.filter(
      (o) => o.transit_status === "subindo" || o.transit_status === "descendo"
    );
  }, [orders]);

  const handleToggle = useCallback(async (order, direction) => {
    const newStatus = (!direction || order.transit_status === direction) ? "" : direction;

    let previousOrders = [];
    if (setOrders) {
      setOrders(prev => {
        previousOrders = [...prev];
        return prev.map(o => 
          o.id === order.id 
            ? { ...o, transit_status: newStatus, updated: new Date().toISOString() } 
            : o
        );
      });
    }

    try {
      await productionService.toggleTransit(order.id, newStatus, user?.name);

      const actionText = newStatus
        ? `Marcou trânsito via Aba Trânsito: ${newStatus.toUpperCase()}`
        : "Removeu trânsito via Aba Trânsito (Chegou)";

      await logAction(
        user,
        "PRODUCAO",
        "TRANSITO",
        `${actionText} - Item ${order.sku}`,
        { itemId: order.id, newStatus }
      );
    } catch (error) {
      if (setOrders && previousOrders.length > 0) setOrders(previousOrders);
      console.error(error);
      alert("Erro ao alterar trânsito na aba: " + error.message);
    }
  }, [user, setOrders]);

  const goingUp = transitItems.filter((i) => i.transit_status === "subindo"); 
  const goingDown = transitItems.filter((i) => i.transit_status === "descendo"); 

  if (loading)
    return <div className="p-10 text-center">Carregando trânsito...</div>;

  return (
    <div className="h-[calc(100vh-64px)] w-full overflow-hidden bg-slate-50 p-4 flex flex-col gap-4">
      <div className="flex justify-between items-center shrink-0">
        <h2 className="text-xl font-bold text-slate-700 flex items-center gap-2">
          <Truck className="text-blue-500" /> Monitoramento de Trânsito
        </h2>
        <div className="text-sm text-slate-500">
          Total em movimento: <b>{transitItems.length}</b>
        </div>
      </div>

      <div className="flex-1 flex flex-col md:flex-row gap-4 overflow-hidden">
        {/* DESCENDO (Fábrica) */}
        <RenderList
          title="Descendo para Fábrica"
          items={goingDown}
          icon={ArrowDown}
          colorClass="bg-orange-50 text-orange-800"
          findCatalogItem={findCatalogItem}
          onToggle={handleToggle}
        />

        {/* SUBINDO (Escritório) */}
        <RenderList
          title="Subindo para Escritório"
          items={goingUp}
          icon={ArrowUp}
          colorClass="bg-emerald-50 text-emerald-800"
          findCatalogItem={findCatalogItem}
          onToggle={handleToggle}
        />
      </div>
    </div>
  );
}
